<template>
  <span
    data-cy="formula-service"
    class="inline-flex items-center space-x-1 rounded-sm bg-gray-100 p-1"
    :class="[
      { 'border border-red-400 bg-white text-red-400': !attribute },
      { 'border border-blue-400': node.isSelected },
      {
        'border border-red-100 bg-red-100': failedEvaluation,
      },
      { 'bg-red-100': !isEmpty(node.error) },
    ]"
  >
    <tooltip v-if="attribute">
      <span class="flex items-center space-x-1">
        <img
          :src="serviceAttributeImg"
          class="h-4 w-4"
          :alt="attributeName"
          loading="lazy"
          draggable="false"
        />
        <span data-node-content class="text-headline-new">
          {{ attributeName }}
        </span>
      </span>

      <template #content>
        <div class="flex flex-col">
          <span>{{ attributeServiceTitleNamespaced }}</span>
          <span v-if="attributeContextValueString">
            {{ attributeContextValueString }}
          </span>
        </div>
      </template>
    </tooltip>

    <span v-else data-node-content>
      {{ t("COMMON.UNKNOWN") }} {{ t("ATTRIBUTE_BUILDER.ATTRIBUTE") }}
    </span>
    <formula-service-context
      v-if="isContextNeeded"
      :item="{ id: node.attributeId, context: node.context }"
      :attribute="attribute || null"
      :is-active="!!node.isSelected"
      :class="{ 'pointer-events-none': disabled }"
      :error="!isEmpty(node.error)"
      @apply="emit('context:apply', $event)"
    />
    <span
      v-if="node.result !== undefined"
      v-tooltip="
        failedEvaluation && isNodeCustomAttribute && customAttributeTooltip
      "
      class="break-word font-semibold text-headline"
      :class="{
        'cursor-pointer': failedEvaluation && isNodeCustomAttribute,
      }"
      data-cy="result"
      @click="scrollToElement"
    >
      : {{ formattedResult }}
    </span>
  </span>
</template>
<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import isEmpty from "lodash/isEmpty";

import { isBaselayerBusinessRegistrationsAttribute } from "@/helpers/baselayer";
import {
  SERVICE_APPLICATION_DATA,
  SERVICE_CUSTOM,
  SERVICE_CUSTOM_ATTRIBUTES,
} from "@/helpers/constants";
import {
  formatNumberWithCommas,
  truncateWithEllipsis,
} from "@/helpers/formatting";
import { isOcrolusFullResponseAttribute } from "@/helpers/ocrolus";
import { getImageForService } from "@/helpers/services";
import { getFullResponseDebtSchedulePlaceholder } from "@/helpers/services";
import { useAttributeBuilderServices } from "@/hooks/attributeBuilder";
import contextToString from "@/lib/dataServices/contextToString";

import FormulaServiceContext from "@/components/customAttributesV2/FormulaServiceContext.vue";

import type { EditorNode } from "@/models/TextEditor";
import type {
  IContextSetValueModel,
  IStepAttribute,
} from "@/models/orchestration";

interface ServiceNode extends Omit<EditorNode, "attributeId" | "context"> {
  attributeId: IStepAttribute["id"];
  context?: IStepAttribute["context"];
  result?: number | string;
}

const emit = defineEmits<{
  "context:apply": [value: IContextSetValueModel];
}>();

const { node, disabled } = defineProps<{
  node: ServiceNode;
  disabled?: boolean;
}>();

const { t } = useI18n();
const { getService, getServiceProviderDetails } = useAttributeBuilderServices();

const isMac = navigator.userAgent.toLowerCase().indexOf("mac") >= 0;

const customAttributeTooltip = `"${isMac ? "Cmd" : "Ctrl"} + ${t(
  "COMMON.CLICK"
)}" ${t("COMMON.TO").toLowerCase()} ${t("COMMON.SCROLL")} ${t(
  "COMMON.TO"
).toLowerCase()} ${t("ATTRIBUTE_BUILDER.ATTRIBUTE")}`;

const attribute = computed(() => getService(node.attributeId, node.context));

const isNodeCustomAttribute = computed(() => {
  return node.attributeId === SERVICE_CUSTOM;
});

const serviceAttributeProviderInfo = computed(() =>
  getServiceProviderDetails(node.attributeId)
);

const serviceAttributeImg = computed(() => {
  if (
    serviceAttributeProviderInfo.value?.category &&
    (
      [SERVICE_APPLICATION_DATA, SERVICE_CUSTOM_ATTRIBUTES] as string[]
    ).includes(serviceAttributeProviderInfo.value?.category)
  ) {
    return getImageForService(serviceAttributeProviderInfo.value?.category);
  } else {
    return getImageForService(serviceAttributeProviderInfo.value?.id);
  }
});

const attributeName = computed(() => attribute.value?.name || "");

const isContextNeeded = computed(() => {
  return (
    !!attribute.value?.options?.consumer_score_models_levels ||
    !!attribute.value?.options?.month_picker_range ||
    !!attribute.value?.options?.in_array_values ||
    !!Object.prototype.hasOwnProperty.call(
      attribute.value?.meta ?? {},
      "specific_year"
    )
  );
});

const attributeServiceTitleNamespaced = computed(() => {
  return [
    serviceAttributeProviderInfo.value?.title,
    serviceAttributeProviderInfo.value?.name,
    serviceAttributeProviderInfo.value?.category,
  ]
    .filter(Boolean)
    .join("/");
});

const attributeContextValueString = computed(() =>
  contextToString(node.context?.value)
);

const formattedResult = computed(() => {
  if (isOcrolusFullResponseAttribute(node.result)) {
    return t(
      "DEALS.DEAL_PROGRESS.UNDERWRITING.OCROLUS.SERVICE_RUN_ATTRIBUTE_PLACEHOLDER"
    );
  }

  if (isBaselayerBusinessRegistrationsAttribute(node.result)) {
    return t("DEALS.BASELAYER.BUSINESS_REGISTRATIONS_JSON");
  }

  const fullResponseDebtSchedulePlaceholder =
    getFullResponseDebtSchedulePlaceholder(node.result);

  if (fullResponseDebtSchedulePlaceholder) {
    return fullResponseDebtSchedulePlaceholder;
  }

  if (typeof node.result === "number") {
    return formatNumberWithCommas(node.result, {
      style: "decimal",
      maximumFractionDigits: 17,
    });
  }
  return truncateWithEllipsis(String(node.result));
});

const failedEvaluation = computed(() =>
  ["#NOT_EVALUATED!", "#DIV/0!", "#ERROR"].includes(node.result as string)
);

const scrollToElement = (event: MouseEvent) => {
  const attributeId = node.context?.value?.id;
  const metaPressed = isMac ? event?.metaKey : event?.ctrlKey;
  if (!attributeId || !metaPressed) {
    return;
  }
  document.getElementById(attributeId)?.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
};
</script>
